/**
 * Formatadores do painel de detalhes da conversa.
 *
 * Puros: sem React, sem rede, sem relógio implícito — `formatWindowRemaining`
 * recebe o "agora" para poder ser testado sem congelar o tempo.
 *
 * Regra geral: **ausência vira travessão**, nunca string vazia e nunca "null".
 * Um travessão diz "não temos esse dado"; um branco parece defeito de layout.
 */

import { formatRemaining } from '../../hooks/useMessagingWindow';

const TRACO = '—';

const moeda = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
});

export function formatCurrencyBRL(value: number | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return TRACO;
  // `Intl` separa "R$" do número com espaço estreito não separável (U+00A0 ou
  // U+202F conforme a versão do ICU). Normalizamos para espaço comum: senão o
  // mesmo texto muda de bytes entre ambientes e a comparação em teste quebra.
  return moeda.format(value).replace(/[  ]/g, ' ');
}

function parseDate(iso: string | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatShortDate(iso: string | undefined): string {
  const d = parseDate(iso);
  return d ? d.toLocaleDateString('pt-BR') : TRACO;
}

export function formatDateTime(iso: string | undefined): string {
  const d = parseDate(iso);
  if (!d) return TRACO;
  const data = d.toLocaleDateString('pt-BR');
  const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `${data} às ${hora}`;
}

/*
 * NÃO existe aqui um `formatWindowRemaining`: a janela de 24h da Meta já tem
 * dono — `useMessagingWindow` (`../../hooks/useMessagingWindow`), que devolve
 * `remainingLabel` já formatado E vivo (recalcula por minuto, reconcilia com o
 * código de falha da Meta, trata contato bloqueado e conversa fechada).
 * Duplicar aqui daria um segundo formato para o mesmo número — o painel diria
 * "23h38" enquanto a faixa do composer diria "23h38min". Quem precisa do rótulo
 * chama o hook e troca string vazia por travessão.
 */

/**
 * Tempo de resposta, no MESMO formato do contador da janela — delega para
 * `formatRemaining`, que já é o dono desse formato ("3 min", "1h35"). Escrever
 * a conta de novo aqui daria duas versões do mesmo texto, livres para divergir.
 * Entra em minutos porque é assim que `firstResponseTime` chega da API.
 */
export function formatResponseTime(minutes: number | undefined): string {
  if (typeof minutes !== 'number' || !Number.isFinite(minutes)) return TRACO;
  return formatRemaining(Math.max(0, minutes) * 60 * 1000);
}

const TEMPERATURAS: Record<string, string> = {
  hot: 'Quente',
  warm: 'Morno',
  cold: 'Frio',
};

/**
 * O tipo de `Lead.temperature` aceita valor livre (`string & {}`) porque vem de
 * integração e importação. Valor desconhecido volta como veio — melhor mostrar
 * o que está gravado do que esconder atrás de um travessão.
 */
export function temperatureLabel(t: string | undefined): string {
  if (!t) return TRACO;
  return TEMPERATURAS[t] ?? t;
}

export function formatFileSize(bytes: number | undefined): string {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) return TRACO;
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(1).replace('.', ',')} MB`;
}
